import { css } from '@emotion/react';
import { i18next } from '@frontend/i18n';
import { TooltipContentProps } from '@frontend/product-onboarding';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS, CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS } from '../constant';

interface HotspotHintConfig {
  title: string;
  description: string;
}

const baseHotspotHintMap: Record<string, HotspotHintConfig> = {
  [CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB]: {
    title: i18next.t('Skip the recording. Never take notes again.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Call Intelligence automatically summarizes and transcribes every eligible call.', {
      ns: 'call-intel-onboarding',
    }),
  },
  [CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD]: {
    title: i18next.t('Be prepared for your next conversation.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Quickly see what was discussed in the last call summary.', {
      ns: 'call-intel-onboarding',
    }),
  },
  [CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_HISTORY_TAB_CALL]: {
    title: i18next.t('Access summaries and transcripts from past conversations.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Click into any past call to see its summary and transcript.', {
      ns: 'call-intel-onboarding',
    }),
  },
  [CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON]: {
    title: i18next.t('Make your team even more effective.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Share Call Intelligence with your team by assigning the Data Analyst role.', {
      ns: 'call-intel-onboarding',
    }),
  },
};

const proHotspotHintMap: Record<string, HotspotHintConfig> = {
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON_TOOLTIP_POPOVER]: {
    title: i18next.t('Know what your data means', { ns: 'call-intel-onboarding' }),
    description: i18next.t(
      'Clicking on any information icon throughout Call Intelligence to see how each metric is defined and why it matters for your practice.',
      {
        ns: 'call-intel-onboarding',
      }
    ),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB]: {
    title: i18next.t('Skip the recording. Never take notes again.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Call Intelligence automatically summarizes and transcribes every eligible call.', {
      ns: 'call-intel-onboarding',
    }),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_SCHEDULING_OUTCOME_LABEL]: {
    title: i18next.t('Turn insights into next steps.', { ns: 'call-intel-onboarding' }),
    description: i18next.t(
      'AI analyzes calls to flag unscheduled opportunities and unhappy patients so you know who to prioritize.',
      {
        ns: 'call-intel-onboarding',
      }
    ),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_EDIT_AI_VIEW_LABEL]: {
    title: i18next.t('Make a correction when something feels off.', { ns: 'call-intel-onboarding' }),
    description: i18next.t(
      'The AI analysis can be corrected if it seems wrong. This sends feedback to the Weave team so we know where to improve.',
      {
        ns: 'call-intel-onboarding',
      }
    ),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL]: {
    title: i18next.t('Report issues to help make improvements.', { ns: 'call-intel-onboarding' }),
    description: i18next.t(
      'Give a quick thumbs or thumbs down and describe the issues so we can learn what’s working and fix what’s not.',
      {
        ns: 'call-intel-onboarding',
      }
    ),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASK_ROW]: {
    title: i18next.t('Make sure nothing slips through the cracks', { ns: 'call-intel-onboarding' }),
    description: i18next.t('AI-generated tasks are auto-assigned to the Office User who handled the call.', {
      ns: 'call-intel-onboarding',
    }),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD]: {
    title: i18next.t('Be prepared for your next conversation.', { ns: 'call-intel-onboarding' }),
    description: i18next.t(
      'See what was discussed last time, how the patient felt, and what still needs to be resolved.',
      {
        ns: 'call-intel-onboarding',
      }
    ),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_HISTORY_TAB_CALL]: {
    title: i18next.t('Get intelligence on past conversations.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Click into any past call to see its summary, transcript, and AI insights.', {
      ns: 'call-intel-onboarding',
    }),
  },
  [CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON]: {
    title: i18next.t('Make your team even more effective.', { ns: 'call-intel-onboarding' }),
    description: i18next.t('Share Call Intelligence with your team by assigning the Data Analyst role.', {
      ns: 'call-intel-onboarding',
    }),
  },
};

const hotspotHintMap: Record<string, HotspotHintConfig> = {
  ...baseHotspotHintMap,
  ...proHotspotHintMap,
};

export function HotspotTooltipContent({ hotspotId }: TooltipContentProps) {
  const config = hotspotHintMap[hotspotId];
  if (!config) return null;
  return (
    <div>
      <Text size='medium' weight='bold' css={styles.title}>
        {config.title}
      </Text>
      <Text size='small' weight='regular'>
        {config.description}
      </Text>
    </div>
  );
}

const styles = {
  title: css`
    white-space: wrap;
    margin-bottom: ${theme.spacing(1)};
  `,
};
